import { useMarbleEnabled } from '#pplx/frontend/apps/ask/hooks/marble-hooks/useMarbleEnabled.js';
import { useSidebarRecentItemsGraphQL } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useSidebarRecentItemsGraphQL.js';
import { useSidebarQueryRecovery } from '#pplx/frontend/apps/ask/hooks/sidebar-graphql-v2/useSidebarQueryRecovery.js';
import { getSidebarV2InitialQueryKey, getSidebarV2ProjectThreadsQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { createSidebarSubscription } from '#pplx/frontend/apps/ask/shared-helpers/createSidebarSubscription.js';
import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { reportAction } from '#pplx/frontend/metrics/browser.js';
import { useInfiniteQuery } from '#pplx/frontend/react-query/useInfiniteQuery.js';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
const source = (page, failed) => ({
    data: page?.items ?? null,
    artifacts: page?.artifacts,
    teammates: page?.teammates,
    status: page ? 'active' : failed ? 'error' : 'loading',
});
function useLiveFeed(key, enabled, open) {
    const [state, setState] = useState({
        key,
        error: false,
    });
    useEffect(() => {
        if (!enabled)
            return;
        const subscription = createSidebarSubscription({
            subscribe: ({ onSnapshot, onError }) => open(onSnapshot, onError),
            onSnapshot: (page) => setState({ key, page, receivedAt: Date.now(), error: false }),
            onError: () => setState((current) => current.key === key ? { ...current, error: true } : { key, error: true }),
        });
        return subscription.retain();
    }, [key, enabled, open]);
    return state.key === key ? state : { key, error: false };
}
export const SidebarV2DataLoader = memo(function SidebarV2DataLoader({ onData, filter, viewer, enabled, }) {
    const client = useQueryClient();
    const includeTeammates = useMarbleEnabled({ defaultValue: false });
    const [pinnedOnly] = useLocalUserSettings('sidebarShowPinnedProjectsOnly', false);
    const recovery = useSidebarQueryRecovery();
    const initial = useInfiniteQuery({
        queryKey: [...getSidebarV2InitialQueryKey(viewer), pinnedOnly, includeTeammates],
        initialPageParam: null,
        queryFn: async ({ pageParam }) => {
            const startedAt = Date.now();
            const revisions = new Map(client
                .getQueryCache()
                .findAll({
                queryKey: getSidebarV2ProjectThreadsQueryKey(),
            })
                .map((query) => [query.queryHash, query.state.dataUpdateCount]));
            const { fetchInitial, fetchProjects } = await import('#pplx/frontend/apps/ask/hooks/sidebar-graphql-v2/client.js');
            const page = pageParam
                ? await fetchProjects(pageParam, pinnedOnly)
                : await fetchInitial(pinnedOnly, includeTeammates);
            for (const project of page.projects ?? []) {
                if (project.threads === undefined)
                    continue;
                const queryKey = [...getSidebarV2ProjectThreadsQueryKey(project.uuid), viewer];
                const cached = client.getQueryCache().find({ queryKey });
                // preserve fresh live/individual reads received while this page was loading.
                if (cached &&
                    cached.state.dataUpdatedAt > 0 &&
                    cached.state.dataUpdateCount !== revisions.get(cached.queryHash))
                    continue;
                if (project.threads === null && cached?.state.data != null)
                    continue;
                client.setQueryData(queryKey, project.threads);
            }
            return { ...page, startedAt };
        },
        getNextPageParam: (page, pages) => pages.length < 2 && page.projectsPageInfo?.hasNextPage
            ? page.projectsPageInfo.endCursor
            : undefined,
        enabled,
        staleTime: 30_000,
        ...recovery,
    });
    const recents = useSidebarRecentItemsGraphQL({ enabled, filter });
    const openPins = useCallback(async (snapshot, onError) => {
        const { subscribePins } = await import('#pplx/frontend/apps/ask/hooks/sidebar-graphql-v2/client.js');
        return subscribePins(snapshot, onError, includeTeammates);
    }, [includeTeammates]);
    const pinsLive = useLiveFeed(`${viewer}:${includeTeammates}`, enabled && initial.data != null, openPins);
    const firstPage = initial.data?.pages[0];
    const pinsPage = (pinsLive.receivedAt ?? 0) >= (firstPage?.startedAt ?? 0)
        ? (pinsLive.page ?? firstPage?.pins ?? undefined)
        : (firstPage?.pins ?? pinsLive.page);
    const projects = useMemo(() => {
        if (firstPage?.projects == null)
            return null;
        const seen = new Set();
        return (initial.data?.pages
            .flatMap((page) => page.projects ?? [])
            .filter((project) => {
            if (seen.has(project.uuid))
                return false;
            seen.add(project.uuid);
            return true;
        }) ?? []);
    }, [firstPage?.projects, initial.data?.pages]);
    const { fetchNextPage, isFetchNextPageError } = initial;
    const loadMoreProjects = useCallback(() => {
        void fetchNextPage();
    }, [fetchNextPage]);
    const retry = useCallback(() => {
        if (isFetchNextPageError)
            void fetchNextPage();
        else
            void client.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey(viewer) });
        void import('#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebarLiveUpdatesManager.js')
            .then(({ retrySidebarRecentItems }) => retrySidebarRecentItems(filter))
            .catch((error) => logger.error('Failed to retry sidebar recent items.', error));
    }, [client, viewer, filter, isFetchNextPageError, fetchNextPage]);
    const pinsFailed = (initial.isError && !initial.isFetchNextPageError) ||
        firstPage?.pins === null ||
        pinsLive.error;
    const refreshTeammates = useCallback(() => {
        void client.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey(viewer) });
    }, [client, viewer]);
    const teammatePage = useMemo(() => includeTeammates && (firstPage || initial.isError)
        ? { page: firstPage?.teammates ?? null, startedAt: firstPage?.startedAt ?? 0 }
        : undefined, [includeTeammates, firstPage, initial.isError]);
    const recentsFailed = recents.status === 'error';
    const projectsFailed = initial.isError || firstPage?.projects === null;
    const teammatesFailed = includeTeammates && (initial.isError || firstPage?.teammates === null);
    const reportedFailures = useRef(new Set());
    useEffect(() => {
        const failedSections = [
            ...(pinsFailed ? ['pins'] : []),
            ...(recentsFailed ? ['recents'] : []),
            ...(projectsFailed ? ['projects'] : []),
            ...(teammatesFailed ? ['teammates'] : []),
        ];
        for (const section of failedSections) {
            if (!reportedFailures.current.has(section)) {
                reportAction('web.frontend.sidebar_graphql_v2.source_error', { section });
            }
        }
        reportedFailures.current = new Set(failedSections);
    }, [pinsFailed, recentsFailed, projectsFailed, teammatesFailed]);
    const value = useMemo(() => ({
        active: enabled,
        teammates: teammatePage,
        refreshTeammates,
        recentsFilter: filter,
        pins: source(pinsPage, pinsFailed),
        recents,
        projects: projects ?? [],
        projectsHasData: projects != null,
        projectsLoading: projects == null && !projectsFailed,
        projectsError: projectsFailed,
        projectsHasNextPage: initial.hasNextPage,
        projectsLoadingMore: initial.isFetchingNextPage,
        loadMoreProjects,
        retry,
    }), [
        enabled,
        teammatePage,
        refreshTeammates,
        filter,
        pinsPage,
        pinsFailed,
        recents,
        projects,
        projectsFailed,
        initial.hasNextPage,
        initial.isFetchingNextPage,
        loadMoreProjects,
        retry,
    ]);
    useLayoutEffect(() => onData(value), [onData, value]);
    return null;
});
//# sourceMappingURL=SidebarV2DataLoader.js.map